import { useCallback } from 'react'
import type { DraftType, UserInputDraft } from '../../state/types'
import { createTimestamps, updateTimestamps } from '../../state/types'

interface UseUserInputDraftsParams {
  readonly drafts: readonly UserInputDraft[]
  readonly onUserInputDraftsChange: (drafts: UserInputDraft[]) => void
}

interface SaveDraftParams {
  readonly refEventId: string | null
  readonly type: DraftType
  readonly content: string
}

interface UseUserInputDraftsReturn {
  readonly drafts: readonly UserInputDraft[]
  readonly saveDraft: (params: SaveDraftParams) => void
  readonly deleteDraft: (draftId: string) => void
  readonly getDraftByRefEventId: (
    refEventId: string | null,
    type: DraftType,
  ) => UserInputDraft | undefined
  readonly clearAllDrafts: () => void
}

function useUserInputDrafts({
  drafts,
  onUserInputDraftsChange,
}: UseUserInputDraftsParams): UseUserInputDraftsReturn {
  const saveDraft = useCallback(
    ({ refEventId, type, content }: SaveDraftParams) => {
      const existingIndex = drafts.findIndex((d) => d.refEventId === refEventId && d.type === type)

      if (existingIndex >= 0) {
        const updated = drafts.map((d, i) =>
          i === existingIndex ? { ...d, content, ...updateTimestamps(d) } : d,
        )
        onUserInputDraftsChange(updated)
      } else {
        const newDraft: UserInputDraft = {
          id: crypto.randomUUID(),
          refEventId,
          type,
          content,
          ...createTimestamps(),
        }
        onUserInputDraftsChange([...drafts, newDraft])
      }
    },
    [drafts, onUserInputDraftsChange],
  )

  const deleteDraft = useCallback(
    (draftId: string) => {
      const filtered = drafts.filter((d) => d.id !== draftId)
      onUserInputDraftsChange(filtered)
    },
    [drafts, onUserInputDraftsChange],
  )

  const getDraftByRefEventId = useCallback(
    (refEventId: string | null, type: DraftType): UserInputDraft | undefined => {
      return drafts.find((d) => d.refEventId === refEventId && d.type === type)
    },
    [drafts],
  )

  const clearAllDrafts = useCallback(() => {
    onUserInputDraftsChange([])
  }, [onUserInputDraftsChange])

  return {
    drafts,
    saveDraft,
    deleteDraft,
    getDraftByRefEventId,
    clearAllDrafts,
  } as const
}

export { useUserInputDrafts }
export type { UseUserInputDraftsParams, UseUserInputDraftsReturn, SaveDraftParams }
